<template>
  <div class="yandex-gpt">
    <div class="yandex-gpt__header">
      <div class="yandex-gpt__logo">Ya</div>
      <div>
        <h3>YandexGPT</h3>
        <p>Подключение для создания тестов из PDF, DOCX и материалов</p>
      </div>
      <span v-if="hasApiKey" class="yandex-gpt__status">
        <span /> Подключено
      </span>
    </div>

    <div class="yandex-gpt__form">
      <label>
        <span>ID каталога Yandex Cloud</span>
        <input
          v-model.trim="folderId"
          type="text"
          placeholder="b1g..."
          :disabled="loading"
        />
        <small>Идентификатор каталога, в котором доступна модель YandexGPT</small>
      </label>

      <label>
        <span>API-ключ сервисного аккаунта</span>
        <input
          v-model.trim="apiKey"
          type="password"
          :placeholder="apiKeyPlaceholder"
          autocomplete="new-password"
          :disabled="loading"
        />
        <small>Ключ хранится на сервере и не возвращается в админку</small>
      </label>
    </div>

    <div class="yandex-gpt__note">
      Для работы сервисному аккаунту нужен доступ к YandexGPT в выбранном каталоге.
      Если ключ уже сохранен, оставьте поле пустым — текущий ключ не изменится.
    </div>

    <div class="yandex-gpt__actions">
      <UButton
        variant="soft"
        :disabled="loading || !hasApiKey"
        :loading="checking"
        @click="checkConnection"
      >
        Проверить подключение
      </UButton>
      <UButton :disabled="loading" :loading="saving" @click="save">
        Сохранить
      </UButton>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { coreApi } from '@/api'
import UButton from '@/components/redesign/ui/UButton.vue'

export default {
  name: 'YandexGpt',
  components: { UButton },
  data() {
    return {
      folderId: '',
      apiKey: '',
      hasApiKey: false,
      apiKeyTail: '',
      saving: false,
      checking: false,
      loadingSettings: false,
    }
  },
  computed: {
    ...mapState('main', ['user']),
    loading() {
      return this.saving || this.checking || this.loadingSettings
    },
    apiKeyPlaceholder() {
      return this.hasApiKey
        ? `Ключ сохранен, последние символы: ${this.apiKeyTail}`
        : 'Введите API-ключ'
    },
  },
  methods: {
    applySettings(settings) {
      this.folderId = settings.folderId || ''
      this.hasApiKey = Boolean(settings.hasApiKey)
      this.apiKeyTail = settings.apiKeyTail || ''
      this.apiKey = ''
    },
    async loadSettings() {
      this.loadingSettings = true
      try {
        const result = await coreApi.getYandexSettings(this.user.id)
        if (!result?.ok) throw new Error(result?.message || 'Не удалось загрузить настройки')
        this.applySettings(result)
      } catch (error) {
        const message = error?.response?.data?.message || error.message || 'Не удалось загрузить настройки'
        this.$toast.error(message)
      } finally {
        this.loadingSettings = false
      }
    },
    async save() {
      if (!this.folderId) {
        this.$toast.error('Укажите ID каталога Yandex Cloud')
        return
      }

      if (!this.apiKey && !this.hasApiKey) {
        this.$toast.error('Укажите API-ключ')
        return
      }

      this.saving = true
      try {
        const result = await coreApi.saveYandexSettings({
          userId: this.user.id,
          folderId: this.folderId,
          apiKey: this.apiKey,
        })
        if (!result?.ok) throw new Error(result?.message || 'Не удалось сохранить настройки')

        this.applySettings(result)
        this.$toast.success('Настройки YandexGPT сохранены')
      } catch (error) {
        const message = error?.response?.data?.message || error.message || 'Не удалось сохранить настройки'
        this.$toast.error(message)
      } finally {
        this.saving = false
      }
    },
    async checkConnection() {
      this.checking = true
      try {
        const result = await coreApi.checkYandexSettings(this.user.id)
        if (!result?.ok) throw new Error(result?.message || 'Подключение не работает')
        this.$toast.success(result.message || 'Подключение работает')
      } catch (error) {
        const message = error?.response?.data?.message || error.message || 'Подключение не работает'
        this.$toast.error(message)
      } finally {
        this.checking = false
      }
    },
  },
  mounted() {
    this.loadSettings()
  },
}
</script>

<style lang="scss" scoped>
.yandex-gpt {
  max-width: 760px;
  padding: 24px;
  background: #FFFFFF;
  border: 1px solid #E5EAF0;
  border-radius: 14px;

  &__header {
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr) auto;
    gap: 14px;
    align-items: center;
    padding-bottom: 22px;
    border-bottom: 1px solid #EDF0F4;

    h3 {
      margin: 0 0 3px;
      color: #202936;
      font-size: 21px;
      font-weight: 600;
    }

    p {
      margin: 0;
      color: #8490A0;
      font-size: 13px;
    }
  }

  &__logo {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    color: #FFFFFF;
    font-size: 18px;
    font-weight: 700;
    background: #FC3F1D;
    border-radius: 12px;
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 7px 10px;
    color: #277A58;
    font-size: 12px;
    font-weight: 500;
    background: #ECF8F2;
    border-radius: 999px;

    span {
      width: 7px;
      height: 7px;
      background: #2BA56F;
      border-radius: 50%;
    }
  }

  &__form {
    display: grid;
    gap: 20px;
    margin-top: 22px;

    label,
    label > span,
    small {
      display: block;
    }

    label {
      margin: 0;
    }

    label > span {
      margin-bottom: 7px;
      color: #475467;
      font-size: 13px;
      font-weight: 500;
    }

    input {
      width: 100%;
      height: 46px;
      padding: 0 13px;
      color: #202936;
      background: #FAFBFC;
      border: 1px solid #DCE2E8;
      border-radius: 9px;

      &:focus {
        background: #FFFFFF;
        border-color: #0088CC;
        outline: none;
      }
    }

    small {
      margin-top: 6px;
      color: #98A2B3;
      font-size: 12px;
    }
  }

  &__note {
    margin-top: 20px;
    padding: 13px 15px;
    color: #667085;
    font-size: 13px;
    line-height: 19px;
    background: #F6F8FA;
    border-radius: 9px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 22px;

    .button {
      width: auto;
      min-width: 180px;
    }
  }

  @media (max-width: 640px) {
    padding: 18px;

    &__header {
      grid-template-columns: 44px minmax(0, 1fr);
    }

    &__status {
      grid-column: 1 / -1;
      width: max-content;
    }

    &__actions {
      flex-direction: column-reverse;

      .button {
        width: 100%;
      }
    }
  }
}
</style>
